
import { Component, Vue } from 'vue-property-decorator';
import { DateTime } from 'luxon';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import { filterAnsweredQuestions, formatAnsweredQuestion } from '@/utils/custom-questions';
import { getRestaurantMetaInfo } from '@/utils/meta';
import { Validate } from '@/types';
import AdditionalDetailsSection from '@/components/inquiry-form/AdditionalDetailsSection.vue';
import CompactNavbar from '@/components/navigation/CompactNavbar.vue';
import ContactDetailsSection from '@/components/inquiry-form/ContactDetailsSection.vue';
import EventDetailsSection from '@/components/inquiry-form/EventDetailsSection.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import Sidebar from '@/components/navigation/Sidebar.vue';

const namespace: string = 'restaurant';
const requestsNotAcceptedCode: number = 1401;

interface InquiryFormData {
	firstName: string;
	lastName: string;
	email: string;
	phone_number: string;
	event_date: Date | null;
	num_people: number | null;
}

@Component<InquiryFormView>({
	components: {
		AdditionalDetailsSection,
		CompactNavbar,
		ContactDetailsSection,
		EventDetailsSection,
		LoadingSpinner,
		Sidebar,
		ValidationObserver
	},
	metaInfo() {
		return getRestaurantMetaInfo(this.restaurant);
	}
})
export default class InquiryFormView extends Vue {
	@Action('fetchInquiryForm', { namespace }) private fetchInquiryForm!: () => Promise<void>;
	@Action('submitInquiryForm', { namespace }) private submitInquiryForm!: (payload: InquiryFormRequestPayload) => Promise<void>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('getInquiryForm', { namespace }) private inquiryForm!: InquiryForm | null;

	$refs!: { observer: Validate; };

	private submitting: boolean = false;
	private fetchError: boolean = false;
	private fetchErrorMessage: string = '';
	private formData: InquiryFormData = {
		firstName: '',
		lastName: '',
		email: '',
		phone_number: '',
		event_date: null,
		num_people: null
	};
	private additionalQuestions: CustomQuestion[] = [];

	private get sections(): { name: string, slug: string }[] {
		const sectionList = [
			{ name: this.$t('menu.inquiry_form.contact_details'), slug: 'contact' },
			{ name: this.$t('menu.inquiry_form.event_details'), slug: 'event' }
		];

		if (this.inquiryForm?.additional_questions.length) {
			sectionList.push({ name: this.$t('menu.inquiry_form.additional_details'), slug: 'additional' });
		}

		return sectionList;
	}

	private get answeredQuestions(): CheckoutCustomQuestion[] {
		return filterAnsweredQuestions(this.additionalQuestions).map((q) => formatAnsweredQuestion(q, true));
	}

	private created() {
		if (this.inquiryForm) {
			this.initAdditionalQuestions();
		}
		else {
			this.fetchForm();
		}
	}

	/**
	 * Fetch the inquiry form data, showing the error state if the request fails
	 *
	 * @return {Promise<void>}
	 */
	private async fetchForm(): Promise<void> {
		try {
			await this.fetchInquiryForm();
			this.initAdditionalQuestions();
		}
		catch (error) {
			const { code, message } = (error as any)?.response?.data || {};
			this.fetchErrorMessage = code === requestsNotAcceptedCode ? message : '';
			this.fetchError = true;
		}
	}

	/**
	 * Initialize the additionalQuestions array from the inquiry form data,
	 * adding answer state properties for each question type
	 *
	 * @return {void}
	 */
	private initAdditionalQuestions(): void {
		if (this.inquiryForm?.additional_questions.length) {
			this.additionalQuestions = this.inquiryForm.additional_questions.map((question: CustomQuestion) => {
				if (question.question_type === 'checkbox') {
					return { ...question, checked: false };
				}

				if (question.question_type === 'timepicker') {
					return { ...question, timeSelected: '' };
				}

				if (question.question_type === 'open') {
					return { ...question, answer: '' };
				}

				return { ...question };
			});
		}
	}

	/**
	 * Update a field on formData, called via update-field events from child section components
	 *
	 * @param {keyof InquiryFormData} field - The formData key to update
	 * @param {any} value - The new value
	 * @return {void}
	 */
	private updateFormField(field: keyof InquiryFormData, value: any): void {
		Vue.set(this.formData, field, value);
	}

	/**
	 * Validate the form, format resquest payload and submit the inquiry form request
	 *
	 * @return {Promise<void>}
	 */
	private async submitForm(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}
		const { firstName, lastName, event_date, num_people, ...rest } = this.formData;
		const payload: InquiryFormRequestPayload = {
			...rest,
			name: `${firstName} ${lastName}`,
			event_date: event_date ? DateTime.fromJSDate(event_date).toFormat('yyyy-MM-dd') : '',
			num_people: num_people as number,
			additional_questions: this.answeredQuestions
		};
		this.submitting = true;
		try {
			await this.submitInquiryForm(payload);
			this.submitting = false;
			this.$toasted.show(this.$t('menu.inquiry_form.success_message'), { position: 'top-center', type: 'success' }).goAway(5000);
			this.$router.push({ path: `/${this.restaurant.slug}`, query: this.$route.query, hash: this.$route.hash }).catch(() => {});
		}
		catch (error) {
			const errorMessage = (error as any)?.response?.data?.message || (error as any).message;
			this.submitting = false;
			this.$toasted.show(this.$t('menu.inquiry_form.error_submitting_form', { errorMessage }), { type: 'error', position: 'top-center' }).goAway(5000);
		}
	}
}
